/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import isNil from 'lodash/isNil';
import get from 'lodash/get';
import mapBy from 'lodash/fp/map';
import keyBy from 'lodash/fp/keyBy';
import sortBy from 'lodash/fp/sortBy';
import filterBy from 'lodash/fp/filter';
import sortedUniqBy from 'lodash/fp/sortedUniqBy';
import { OpenInNew } from '@mui/icons-material';
import { fetchTickets, toggleTicketsFilter } from '../../store/tickets/actions';
import { selectSalesforceBaseURL, selectTerminals, selectTicketsOptions, selectTicketsTotal } from '../../store';
import Refresh from '../common/table/Refresh';
import FilterSelect, { FilterSelectItem } from '../common/filter/FilterSelect';
import Terminal, { InstallationType } from '../../shared/entities/terminal';
import { getTerminals } from '../../store/terminals/actions';
import FilterList from '../common/filter/FilterList';
import StyledProps from '../common/theme/AppThemeInterface';

export interface TicketsViewHeaderContentsProps extends RouteComponentProps {}

const keyByKey = keyBy<FilterSelectItem>('key');

const mapTerminalToItem = (terminals: Terminal[], fieldName: string): FilterSelectItem[] => {
  return mapBy<Terminal, FilterSelectItem>(terminal => {
    // Most terminals are filtered by aircraft tail number.
    // Dual terminals will use their terminal display name
    // to filter, since tickets are expected to contain the
    // display name as the "tailNumber" field.
    const filterSelectItemValue =
      terminal.installationType === InstallationType.DUAL_TERMINAL
        ? terminal.terminalDisplayName
        : terminal.aircraftTailNumber;
    return {
      key: filterSelectItemValue,
      label: get(terminal, fieldName),
      path: ['tickets', 'tailNumber', filterSelectItemValue],
      parentKey: 'tailNumber',
      parentLabel: 'Terminal',
    };
  })(terminals);
};

const getUniqueTerminals = (terminals: { [key: string]: Terminal }, fieldName: string): Terminal[] =>
  // @ts-ignore
  sortedUniqBy<Terminal>(fieldName)(filterBy<Terminal>(fieldName)(sortBy<Terminal>(fieldName)(terminals)));

export function TicketsViewHeaderContents() {
  const dispatch = useDispatch();

  const total = useSelector(selectTicketsTotal, shallowEqual);
  const options = useSelector(selectTicketsOptions, shallowEqual);
  const salesforceBaseURL = useSelector(selectSalesforceBaseURL, shallowEqual);
  const terminals = useSelector(selectTerminals, shallowEqual);

  useEffect(() => {
    dispatch(getTerminals());
  }, [dispatch]);

  const getMenu = (): FilterSelectItem => {
    const fieldName = 'terminalDisplayName';

    const uniqueTerminals = getUniqueTerminals(terminals, fieldName);

    const tailNumber: FilterSelectItem = {
      label: 'Terminal',
      key: 'tailNumber',
      path: ['tickets', 'tailNumber'],
    };

    tailNumber.children = keyByKey(mapTerminalToItem(uniqueTerminals, fieldName));

    return {
      label: 'Filter Tickets By',
      key: 'tickets',
      path: ['tickets'],
      children: {
        tailNumber,
        priority: {
          label: 'Priority',
          key: 'priority',
          path: ['tickets', 'priority'],
          children: {
            URGENT: {
              label: 'Urgent',
              key: 'URGENT',
              path: ['tickets', 'priority', 'URGENT'],
              parentKey: 'priority',
              parentLabel: 'Priority',
            },
            HIGH: {
              label: 'High',
              key: 'HIGH',
              path: ['tickets', 'priority', 'HIGH'],
              parentKey: 'priority',
              parentLabel: 'Priority',
            },
            MEDIUM: {
              label: 'Medium',
              key: 'MEDIUM',
              path: ['tickets', 'priority', 'MEDIUM'],
              parentKey: 'priority',
              parentLabel: 'Priority',
            },
            LOW: {
              label: 'Low',
              key: 'LOW',
              path: ['tickets', 'priority', 'LOW'],
              parentKey: 'priority',
              parentLabel: 'Priority',
            },
            OTHER: {
              label: 'Other',
              key: 'OTHER',
              path: ['tickets', 'priority', 'OTHER'],
              parentKey: 'priority',
              parentLabel: 'Priority',
            },
          },
        },
      },
    };
  };

  const menu = getMenu();
  const toggleFilter = (item: FilterSelectItem) => {
    const itemKey = item.path[item.path.length - 1];
    const parentKey = item.path[item.path.length - 2];
    dispatch(toggleTicketsFilter({ key: parentKey, value: itemKey }));
  };

  return (
    <React.Fragment>
      <h1>
        Open Tickets
        <span className="viewheader__count"> ({total})</span>
      </h1>
      <Refresh onClick={() => dispatch(fetchTickets())} />
      {!isNil(options) ? (
        <React.Fragment>
          <FilterSelect menu={menu} filters={options.filters} onSelect={toggleFilter} />
          <FilterList menu={menu} filters={options.filters} onSelect={toggleFilter} />
        </React.Fragment>
      ) : null}
      <SalesforceLink href={`${salesforceBaseURL}/`} target="_blank">
        <OpenInNew /> Salesforce
      </SalesforceLink>
    </React.Fragment>
  );
}

export default TicketsViewHeaderContents;

const SalesforceLink = styled.a`
  color: ${({ theme }: StyledProps) => theme.externalLinkColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: 14px;
  text-decoration: none;
  display: flex;
  align-items: center;
  text-align: right;
  text-transform: uppercase;
  margin-left: auto;
  margin-right: 70px;

  &:hover {
    text-decoration: underline;
  }

  svg {
    height: 16px;
  }
`;
